import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  requestCreateTranslationCatalog,
  requestDeleteTranslationCatalog,
  requestDeleteTranslationCatalogField,
  requestGetTranslationCatalog,
  requestGetTranslationCatalogs,
  requestLockTranslationCatalog,
  requestPatchTranslationCatalog,
  requestTranslateTranslationCatalog,
  requestUnlockTranslationCatalog,
  requestUploadTranslationCatalog,
} from '~/queries/customerCommunication/requestTranslationCatalog';

import type {
  TranslationCatalog,
  TranslationCatalogCreatePayload,
  TranslationCatalogPatch,
} from '~/components/CustomerCommunication/TranslationCatalog/types';

export const translationCatalogQueryKeys = {
  all: ['translationCatalogs'] as const,
  lists: () => [...translationCatalogQueryKeys.all, 'list'] as const,
  list: () => [...translationCatalogQueryKeys.lists()] as const,
  details: () => [...translationCatalogQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...translationCatalogQueryKeys.details(), id] as const,
};

async function cancelTranslationCatalogDetailQuery(queryClient: QueryClient, id?: string): Promise<void> {
  if (!id) {
    return;
  }

  await queryClient.cancelQueries({ queryKey: translationCatalogQueryKeys.detail(id) });
}

export function useTranslationCatalogs() {
  return useQuery<Array<TranslationCatalog>>({
    queryKey: translationCatalogQueryKeys.list(),
    queryFn: ({ signal }) => requestGetTranslationCatalogs(signal),
  });
}

export function useTranslationCatalog(id?: string) {
  return useQuery<TranslationCatalog>({
    queryKey: translationCatalogQueryKeys.detail(id ?? ''),
    queryFn: ({ signal }) => requestGetTranslationCatalog(id, signal),
    enabled: !!id,
  });
}

export function useCreateTranslationCatalog() {
  const queryClient = useQueryClient();

  return useMutation<TranslationCatalog, Error, TranslationCatalogCreatePayload>({
    mutationFn: requestCreateTranslationCatalog,
    onSuccess: (catalog) => {
      queryClient.invalidateQueries({ queryKey: translationCatalogQueryKeys.lists() });
      queryClient.setQueryData(translationCatalogQueryKeys.detail(catalog.id), catalog);
    },
  });
}

export function usePatchTranslationCatalog(id?: string) {
  const queryClient = useQueryClient();

  return useMutation<TranslationCatalog, Error, TranslationCatalogPatch>({
    mutationFn: (payload) => requestPatchTranslationCatalog(id, payload),
    onMutate: async () => {
      await cancelTranslationCatalogDetailQuery(queryClient, id);
    },
    onSuccess: (catalog) => {
      queryClient.invalidateQueries({ queryKey: translationCatalogQueryKeys.lists() });
      queryClient.setQueryData(translationCatalogQueryKeys.detail(catalog.id), catalog);
    },
  });
}

export function useDeleteTranslationCatalog(id?: string) {
  const queryClient = useQueryClient();

  return useMutation<void, Error>({
    mutationFn: () => requestDeleteTranslationCatalog(id),
    onMutate: async () => {
      await cancelTranslationCatalogDetailQuery(queryClient, id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: translationCatalogQueryKeys.lists() });
      if (id) {
        queryClient.removeQueries({ queryKey: translationCatalogQueryKeys.detail(id) });
      }
    },
  });
}

export function useDeleteTranslationCatalogField(id?: string) {
  const queryClient = useQueryClient();

  return useMutation<TranslationCatalog, Error, string>({
    mutationFn: (fieldName) => requestDeleteTranslationCatalogField(id, fieldName),
    onMutate: async () => {
      await cancelTranslationCatalogDetailQuery(queryClient, id);
    },
    onSuccess: (catalog) => {
      queryClient.invalidateQueries({ queryKey: translationCatalogQueryKeys.lists() });
      queryClient.setQueryData(translationCatalogQueryKeys.detail(catalog.id), catalog);
    },
  });
}

export function useTranslateTranslationCatalog(id?: string) {
  const queryClient = useQueryClient();

  return useMutation<TranslationCatalog, Error>({
    mutationFn: () => requestTranslateTranslationCatalog(id),
    onMutate: async () => {
      await cancelTranslationCatalogDetailQuery(queryClient, id);
    },
    onSuccess: (catalog) => {
      queryClient.invalidateQueries({ queryKey: translationCatalogQueryKeys.lists() });
      queryClient.setQueryData(translationCatalogQueryKeys.detail(catalog.id), catalog);
    },
  });
}

export function useLockTranslationCatalog(id?: string) {
  const queryClient = useQueryClient();

  return useMutation<TranslationCatalog, Error>({
    mutationFn: () => requestLockTranslationCatalog(id),
    onMutate: async () => {
      await cancelTranslationCatalogDetailQuery(queryClient, id);
    },
    onSuccess: (catalog) => {
      queryClient.invalidateQueries({ queryKey: translationCatalogQueryKeys.lists() });
      queryClient.setQueryData(translationCatalogQueryKeys.detail(catalog.id), catalog);
    },
  });
}

export function useUnlockTranslationCatalog(id?: string) {
  const queryClient = useQueryClient();

  return useMutation<TranslationCatalog, Error>({
    mutationFn: () => requestUnlockTranslationCatalog(id),
    onMutate: async () => {
      await cancelTranslationCatalogDetailQuery(queryClient, id);
    },
    onSuccess: (catalog) => {
      queryClient.invalidateQueries({ queryKey: translationCatalogQueryKeys.lists() });
      queryClient.setQueryData(translationCatalogQueryKeys.detail(catalog.id), catalog);
    },
  });
}

export function useUploadTranslationCatalog(id?: string) {
  const queryClient = useQueryClient();

  return useMutation<TranslationCatalog, Error>({
    mutationFn: () => requestUploadTranslationCatalog(id),
    onMutate: async () => {
      await cancelTranslationCatalogDetailQuery(queryClient, id);
    },
    onSuccess: (catalog) => {
      queryClient.invalidateQueries({ queryKey: translationCatalogQueryKeys.lists() });
      queryClient.setQueryData(translationCatalogQueryKeys.detail(catalog.id), catalog);
    },
  });
}
